import { Button, Dropdown, GlobalLoading, Modal } from '@/shared/components';
import Image from 'next/image';
import SDS from '@/assets/images/pdp/sds.svg';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { useContext, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/system';
import LanguageContext from '@/context/language';
import { useQuerySDSDocumentLink } from '@/hooks/product-detail.hook';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import { COUNTRY } from '@/app/utils/constant';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { pdpInteractions } from '@/shared/tracking/page-load';
import { getFileNameFromUrl } from '@/shared/tracking/utils';
import { useCountry } from '@/hooks/use-country';
import { useAsyncEffect } from 'ahooks';
import Link from 'next/link';

interface IProps {
  className?: string;
  childCatalogNumbers: string[];
  showPopUp?: boolean;
  onClose?: () => void;
  PDPPosition?: string;
}

const StyledModal = styled(Modal)`
  & .c-dropdown {
    min-height: 80px;
    height: 100%;
    width: 48%;
  }
  & .c-dropdown__options-container {
    max-height: 200px;
    overflow: auto;
    width: 42%;
  }
  & .c-modal__buttons {
    margin: 0px;
  }
`;

function SDSButton(props: IProps) {
  const { className, showPopUp, childCatalogNumbers, PDPPosition } = props;
  const { t } = useLang(NAMESPACE.pdp);
  const [openModal, setOpenModal] = useState(false);
  const { lang } = useContext(LanguageContext);
  const [selectLanguage, setSelectLanguage] = useState('');
  const [selectChildSku, setSelectChildSku] = useState('');
  const [noData, setNoData] = useState(false);
  const { mutateAsync, isLoading } = useQuerySDSDocumentLink();
  const { trackEvent } = useEventTracking();
  const router = useRouter();
  const { isCN, isKR } = useCountry();
  const [SDSLink, setSDSLink] = useState('');
  const languageOptions = {
    cn: [
      {
        language: 'en',
        label: t('english'),
        value: 'en-cn',
      },
      {
        language: 'zh',
        label: t('chinese'),
        value: 'zh',
      },
    ],
    kr: [
      {
        language: 'en',
        label: t('english'),
        value: 'en-kr',
      },
      {
        language: 'ko',
        label: t('korean'),
        value: 'ko',
      },
    ],
  };
  const currentLanguageOptionByCountry = languageOptions[COUNTRY] || [];
  useEffect(() => {
    const selectLanguage = currentLanguageOptionByCountry.find((language) => language.language === lang)?.value ?? 'en';
    setSelectLanguage(selectLanguage);
  }, [lang]);

  useAsyncEffect(async () => {
    if (isCN && childCatalogNumbers.length > 0) {
      const downloadSdsRes = await mutateAsync({
        childSkus: childCatalogNumbers?.join(','),
        language: lang === 'zh' ? 'zh' : 'en-cn',
      });
      setSDSLink(downloadSdsRes.data);
      if (showPopUp) {
        if (downloadSdsRes.data) {
          window.open(downloadSdsRes.data, '_blank');
        } else {
          setOpenModal(true);
        }
      }
    }
    if (showPopUp && isKR) {
      setOpenModal(true);
    }
  }, [isCN, childCatalogNumbers, showPopUp]);

  useEffect(() => {
    isLoading ? GlobalLoading.show() : GlobalLoading.hide();
  }, [isLoading]);
  const handleCloseModal = () => {
    setOpenModal(false);
    props.onClose && props.onClose();
  };

  const handleClickSDS = () => {
    trackEvent({
      key: 'eventsData',
      action: 'PDPInteraction',
      payload: {
        event_name: 'PDPInteraction',
        PDP_interaction_type: pdpInteractions[PDPPosition],
      },
    });
    if (childCatalogNumbers?.length > 0) {
      setOpenModal(true);
    }
  };

  const changeLanguage = (e) => {
    setSelectLanguage(e);
  };

  const changeCatalogNumber = (e) => {
    setSelectChildSku(e);
  };

  const handleDownload = async () => {
    const downloadSdsRes = await mutateAsync({
      childSkus: childCatalogNumbers?.join(','),
      language: selectLanguage,
    });

    if (downloadSdsRes.data !== '' && downloadSdsRes.data !== null) {
      try {
        // code for tracking document download
        const fileName = getFileNameFromUrl(downloadSdsRes.data, selectChildSku);
        trackEvent({
          key: 'eventsData',
          action: 'documentDownloaded',
          payload: {
            event_name: 'documentDownloaded',
            download_type: 'SDS',
            download_name: fileName,
            download_link: downloadSdsRes.data,
          },
        });
      } catch (e) {
        console.error('Error in tracking document download', e);
      }

      window.open(downloadSdsRes.data);
      handleCloseModal();
      GlobalLoading.hide();
    } else {
      setNoData(true);
    }
  };
  const handleClick = () => {
    Cookies.set('request_type', 'msds');
    router.push('/request-form');
  };
  const disableSaveButton = useMemo(() => !(selectLanguage && selectChildSku), [selectLanguage, selectChildSku]);

  return (
    <div className={className}>
      {!showPopUp && (
        <>
          {(!isCN || !SDSLink) && (
            <Button
              type="link"
              className="flex items-center gap-x-4 p-0 text-left h-auto leading-none font-bold"
              onClick={handleClickSDS}
            >
              <Image src={SDS} alt="" width={16} height={16} />
              {t('SDS')}
            </Button>
          )}
          {isCN && SDSLink && (
            <Link
              href={SDSLink}
              target="_blank"
              className={`flex items-center gap-x-4 p-0 text-left h-auto leading-none font-bold`}
            >
              <Image src={SDS} alt="" width={16} height={16} />
              {t('SDS')}
            </Link>
          )}
        </>
      )}
      <StyledModal
        title={isCN ? '' : t('select.download.options')}
        className="pdp_sds_button_modal"
        isOpen={openModal}
        isHiddenCancelButton={isCN}
        isHiddenSaveButton={isCN}
        handleClose={handleCloseModal}
        handleCancel={handleCloseModal}
        handleSave={handleDownload}
        saveButtonLabel={t('download')}
        cancelButtonLabel={t('cancel')}
        disableSaveButton={disableSaveButton}
      >
        {!isCN && (
          <div className="flex">
            <Dropdown
              label={t('catalog.Number')}
              value={selectChildSku}
              className="w-[48%]"
              onSelect={changeCatalogNumber}
              popperContainer={() => document.getElementsByClassName('pdp_sds_button_modal')[0]}
              options={childCatalogNumbers?.map((item) => ({
                label: item,
                value: item,
              }))}
            ></Dropdown>
            <div className="flex-1" />
            <Dropdown
              label={t('language')}
              value={selectLanguage}
              onSelect={changeLanguage}
              popperContainer={() => document.getElementsByClassName('pdp_sds_button_modal')[0]}
            >
              {currentLanguageOptionByCountry?.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </Dropdown>
          </div>
        )}
        {(noData || (isCN && !SDSLink)) && (
          <span className="flex">
            {t('no.data.found')}
            <Button onClick={handleClick} type="link" className="p-0 text-left flex items-center h-auto leading-none">
              {t('req.sds')}
            </Button>
          </span>
        )}
      </StyledModal>
    </div>
  );
}

export { SDSButton };
